/**
 * Outbox v2 — the drainer (ATL-1445 stage 3, P3; plan §2 "Ordering", "How failures are classified").
 *
 * ONE strict FIFO per organization per device, ONE request in flight. Per-document ordering is not
 * enough once Supabase is the master: a sale can reference a client created offline, a return its
 * sale, and the backend's heal-from-Firestore refuses a cut-over organization, so causal order must
 * hold ACROSS documents. One queue gives that for free; the price is that a stuck head blocks
 * everything behind it, which is exactly the "hold the queue and alert" the design asks for.
 *
 * Nothing is ever dropped. The only way an entry leaves the store here is `delivered`
 * (`outboxClassify.ts`). There is no attempt cap, no age cap and no size cap.
 *
 * ── One drainer per organization per JS context (review 1 S2) ─────────────────────────────────
 * "One request in flight" is a property of the DRAINER, so two drainers for one organization break
 * it: each marks its own head in flight and they can land two updates of one document out of order
 * (measured: applied = [B:v1, B:v2, A:v1] — the document ends on v1). So:
 *  - `createOutboxDrainer` refuses a second live drainer for an organization
 *    (`OutboxDrainerConflictError`);
 *  - `stop()` returns a promise that settles only once the pass in flight has ENDED (its send has
 *    returned and its outcome is recorded);
 *  - a drainer created for the organization after a `stop()` waits for that promise before its
 *    first pass, so it can never overlap the one it replaces (sign-out and back, an org switch).
 * On web every tab is its own JS context; the Web Lock below is what serialises tabs.
 *
 * ── One pass ───────────────────────────────────────────────────────────────────────────────────
 * List the organization's queue; look at the HEAD only:
 *  - an unreadable record (`corrupt`, or a `stray` file with no position) → STOP and alert. Both
 *    block (P2 obligation 6): a corrupt record at a seq may be a sale that others depend on, and a
 *    stray sorts first precisely because its position is unknown. Only an operator's
 *    `removeCorrupt` clears it. The alert is deduplicated through the persisted `alertLedger`, so a
 *    restart does not re-page for the same record.
 *  - an entry queued by another install (`foreignInstall`, a restored backup) → HOLD it with
 *    `lastError.code = 'foreign-install'` and STOP (P2 obligation 2). It is never sent until an
 *    operator adopts it (`outboxOperator.ts`); the drainer never adopts (P2 obligation 3).
 *  - an ABSOLUTE stock request whose persisted `origin` is not `live` (a migrated v1 mirror, a
 *    converted 409) → HOLD it without sending (`absolute-replay`, plan §2: never replay an absolute
 *    level onto the ledger after the fact). `live` is provenance, not recency: a v2 writer on this
 *    device queued it, in this session or an earlier one. Stage-2e constraint: 2e retires the absolute
 *    stock MIRROR for cut-over orgs, so a `live` absolute stock entry may only ever be a revision or a
 *    manual count. No v2 writer may enqueue `/products/stock/bulk` as a mirror before 2e (P13 checks).
 *  - `held` → an OPERATOR-ONLY hold (`isOperatorOnlyHold`: an absolute STOCK request) is never resent
 *    by anything here (review 1 S4) — only `outboxOperator.resolveOperatorHold`. Any other hold is
 *    retried at `nextAttemptAt` (30 minutes after it was held), or at once on `app-start` (which
 *    covers "every new bundle"), `deploy` (a new backend version) and `manual`. Otherwise STOP.
 *  - `pending` → sent when due. `enqueue`, `foreground`, `online`, `live-success`, `app-start`,
 *    `manual` and a delivery just before it ignore the backoff ("an immediate retry on foreground or
 *    after any successful request").
 *  - `in-flight` → a send the process did not see finish (it was killed). Sent again at once: the
 *    backend's idempotency on the device-minted id makes that a replay, not a second write. If it
 *    was a HELD entry's retry (`heldSince` set), it is still held for every outcome but delivery.
 *
 * Before sending, the head is marked `in-flight` (and `attempts` bumped) durably, so a delete
 * appended meanwhile cannot purge it (`appendDelete` never purges in-flight entries). If that mark
 * finds the entry gone — a delete purged it, an operator removed it — the pass re-lists: the store
 * never resurrects an absent entry.
 *
 * Outcome of the send:
 *  - `delivered` → `remove`, and the next entry is sent at once.
 *  - `retry` → back to `pending` with backoff 2 s, 4 s, … capped at 5 minutes; STOP. (A held entry
 *    whose retry fails transiently stays held: the refusal is still what a person must see.)
 *  - `held` → `held`, `heldSince` kept from the first time; STOP. Alerted on the transition and
 *    whenever the refusal changes, not on every 30-minute retry.
 *  - `master-changed` → the entry keeps its state, the drainer PAUSES and calls `onMasterChanged`.
 *    It sends nothing until `resumeAfterMasterCheck()` (P5 re-reads the flag and decides).
 *
 * ── Escalation (review 1 S6, review 2; plan §9 risk 3) ─────────────────────────────────────────
 * A head that retries forever is a stop nobody is told about: a 403, a 5xx that never clears, a 404
 * the backend did not produce, a browser dropping the request at CORS preflight. So the queue is
 * `escalated` — a Sentry error and a PostHog event (once per head entry, persisted), and the banner
 * stops promising "will be sent automatically" — once it has made NO PROGRESS for `ESCALATE_AFTER_MS`
 * (1 h) WHILE ONLINE. Precisely: at a STOP point of a pass whose head is merely retrying
 * (`stoppedBy === null`; a held, unreadable or foreign head has its own alert and never also
 * escalates), when `now - max(oldest enqueuedAt, onlineSince, lastDeliveredAt) >= 1 h`.
 *  - `onlineSince` starts at the first evidence of connectivity — a server answered a send (any
 *    status), or `isOnline() === true` (web `navigator.onLine`) — and is RESET by evidence of being
 *    offline: a send no server answered while `isOnline()` is not `true`, or `isOnline() === false`.
 *    So 3 h offline and then one 502 is minute 0, not hour 3; and a web tab whose every request dies
 *    at preflight while `navigator.onLine` is true is paged after an hour.
 *  - `lastDeliveredAt`: a queue that is delivering is draining, not stuck. A backlog that empties never
 *    shows "failing", and the status published before a send drops the flag unless the head is the
 *    very entry that escalated (a stuck head keeps its banner between retries).
 *  - Both clocks are in memory: a restart restarts them (the hourly `outbox_v2_state` still carries
 *    the oldest entry's absolute age).
 *
 * ── Rules this file keeps ──────────────────────────────────────────────────────────────────────
 *  - The store is obtained from `openStore()` at the start of EVERY pass and never kept across one
 *    (P2 obligation 8: a store kept across `close()` refuses every call).
 *  - Web: every pass runs under the Web Lock `atlas-outbox-v2-drain:<org>` taken `ifAvailable`, so
 *    one tab drains. A tab that finds it taken still READS the queue and publishes its status (the
 *    banner shows in every tab, review 1 S7), sends nothing, and looks again later.
 *  - Single-flight: a trigger during a pass is merged into one more pass, never lost — including
 *    one that arrives in the same tick the loop decides to exit (review 1 S3).
 *  - P8: while a v1 → v2 migration of the organization runs in this JS context, nothing is sent — the
 *    check runs before the lock request and before every entry. The pass ends, publishes the queue as
 *    `stoppedBy: 'v1-migration'`, and ONE resume per migration runs a pass afterwards with the release
 *    flags the stopped passes still held, merged (an `app-start` that met the migration still releases a
 *    held entry at once — review 3, SF-1). If a run is going when the resume fires, it merges into its
 *    `rerun`: at most one extra pass.
 *  - The write mode is decided from the entry's `origin` (`writeModeFor`), exhaustively.
 *  - It never reads Remote Config: the dual-write gate (`dual_write_enabled`) must not be able to
 *    drop a master write (plan §1).
 *
 * WIRING IS NOT HERE (dark, ATL-1445 stage 3 P3). The drainer receives its `send` (P5 binds
 * `typedClient.replayRequest(request, { writeMode })`), its store opener, its sink, its alert
 * ledger and its triggers. Nothing in the bundle constructs one yet.
 */
import type { WriteMode } from '@atlas/api-client'

import type { OutboxAlertLedger, OutboxAlertSink, OutboxStateReport } from './outboxAlerts'
import {
  classifyFailure,
  classifySuccess,
  isAbsoluteStockRequest,
  isOperatorOnlyHold,
  type DeliveryOutcome,
  type HeldReason,
} from './outboxClassify'
import type { OutboxEntryOrigin, OutboxEntryV2, OutboxLastError, OutboxRequest, OutboxWriteKind } from './outboxEntry'
import type { OutboxSlot, OutboxStore } from './outboxStore'
import { v1MigrationInProgress } from './v1MigrationBarrier'

export const RETRY_BASE_MS = 2_000
export const RETRY_CAP_MS = 5 * 60_000
export const HELD_RETRY_MS = 30 * 60_000
export const STATE_REPORT_INTERVAL_MS = 60 * 60_000
/** Another tab holds the drain lock, or a pass failed on storage: look again after this. */
export const RECHECK_MS = 30_000
/** Plan §9 risk 3: page when the oldest entry is older than this while the device is online. */
export const ESCALATE_AFTER_MS = 60 * 60_000
/** The `lastError.code` of an entry held because another install queued it. */
export const FOREIGN_INSTALL_CODE = 'foreign-install'
/** The `lastError.code` of a non-live absolute stock request held on sight. */
export const ABSOLUTE_REPLAY_CODE = 'absolute-replay'

export type DrainTrigger =
  | 'app-start'
  | 'enqueue'
  | 'foreground'
  | 'online'
  | 'live-success'
  | 'timer'
  | 'deploy'
  | 'manual'

/** Triggers after which a pending entry is sent regardless of its backoff. */
const RELEASES_BACKOFF: ReadonlySet<DrainTrigger> = new Set<DrainTrigger>([
  'app-start',
  'enqueue',
  'foreground',
  'online',
  'live-success',
  'manual',
])
/** Triggers after which a (retryable) held entry gets one retry before its 30 minutes are up. */
const RELEASES_HELD: ReadonlySet<DrainTrigger> = new Set<DrainTrigger>(['app-start', 'deploy', 'manual'])

/** Every origin is a master write today; a new origin must decide here (compile error otherwise). */
export function writeModeFor(origin: OutboxEntryOrigin): WriteMode {
  switch (origin) {
    case 'live':
    case 'v1-migrated':
    case 'mirror-409-converted':
      return 'master'
  }
}

/**
 * The Web Lock one tab drains an organization's queue under (taken `ifAvailable`). P8's v1 → v2 migration
 * takes it too, WAITING, for its whole copy and v1 removal (review 2, SF-1), so no tab's drainer can
 * deliver a copy whose v1 original is still queued.
 */
export const outboxV2DrainLockName = (orgFirestoreId: string): string => `atlas-outbox-v2-drain:${orgFirestoreId}`

export function backoffMs(attempts: number): number {
  const exponent = Math.max(0, attempts - 1)
  return Math.min(RETRY_CAP_MS, RETRY_BASE_MS * 2 ** Math.min(exponent, 30))
}

export interface OutboxQueueStatus {
  /** Records in this organization's queue, unreadable ones included. */
  depth: number
  /** Entries in state `held` (foreign-install and operator-only holds included). */
  held: number
  /** Held entries only an operator may release (absolute stock requests). */
  operatorHeld: number
  /** Unreadable records (corrupt or stray). */
  blocked: number
  foreignInstall: number
  /** Epoch ms of the oldest readable entry's `enqueuedAt`, or null. */
  oldestEnqueuedAt: number | null
  /**
   * Why the head is not moving, when it is not. `v1-migration` (P8 review 3, nit 1): a v1 → v2 migration
   * of this organization is copying in this JS context; the drainer sends nothing until it has settled
   * and then resumes on its own. A wait, not a stop a person must act on (the banner treats it as
   * WAITING, the P5 watcher as `draining`); a migration that does not end is paged by its own watchdog.
   */
  stoppedBy: 'held' | 'blocked' | 'foreign-install' | 'master-changed' | 'v1-migration' | null
  paused: 'master-changed' | null
  /** The oldest entry has waited `ESCALATE_AFTER_MS` while online (see the module docblock). */
  escalated: boolean
  /** False until a pass (or a read-only look) has read the queue. */
  known: boolean
  /** True when this context read the queue without holding the drain lock (another tab drains). */
  readOnly: boolean
}

const EMPTY_STATUS: OutboxQueueStatus = Object.freeze({
  depth: 0,
  held: 0,
  operatorHeld: 0,
  blocked: 0,
  foreignInstall: 0,
  oldestEnqueuedAt: null,
  stoppedBy: null,
  paused: null,
  escalated: false,
  known: false,
  readOnly: false,
})

type LockManagerLike = {
  request: (name: string, options: { ifAvailable?: boolean }, cb: (lock: unknown) => Promise<unknown>) => Promise<unknown>
}

function defaultLocks(): LockManagerLike | null {
  try {
    const nav = (globalThis as { navigator?: { locks?: LockManagerLike } }).navigator
    return nav?.locks ?? null
  } catch {
    return null
  }
}

/** `navigator.onLine` where the platform has it (web); `null` = unknown (React Native). */
function defaultIsOnline(): boolean | null {
  try {
    const online = (globalThis as { navigator?: { onLine?: unknown } }).navigator?.onLine
    return typeof online === 'boolean' ? online : null
  } catch {
    return null
  }
}

export interface OutboxDrainerOptions {
  orgFirestoreId: string
  /** Called at the start of every pass; its result is never kept past the pass. */
  openStore: () => OutboxStore
  /** Sends one queued request. Rejects exactly as the transport does (raw axios shapes). */
  send: (request: OutboxRequest, options: { writeMode: WriteMode }) => Promise<unknown>
  sink: OutboxAlertSink
  /** Persisted alert dedupe (blocked records, escalations): a restart must not re-page. */
  alertLedger: OutboxAlertLedger
  /** P5: re-read the write-master flag. The drainer stays paused until `resumeAfterMasterCheck`. */
  onMasterChanged?: (detail: { code: string; status: number | null; label: string }) => void
  /**
   * P6: the server acknowledged `entry` (outcome `delivered`), at `at` — called BEFORE the entry is
   * removed from the store, because the server has it whether or not the removal succeeds. The local
   * view moves the entry to "acked, not yet confirmed" (`global/Write/localView.ts`). An observer only:
   * a throw is caught and logged, and never changes what the drainer does.
   */
  onDelivered?: (entry: OutboxEntryV2, at: number, response: unknown) => void
  /**
   * P9: a send put `entry` into `held` (a deterministic refusal, `lastError`), at `at`. The seam's
   * waiter for a live write rejects on it (§1 step 5: "a deterministic refusal rejects and the entry is
   * held"). An observer only, like `onDelivered`: a throw is caught and logged.
   */
  onHeld?: (entry: OutboxEntryV2, lastError: OutboxLastError, at: number) => void
  /**
   * P6: called right BEFORE each pass's (and each read-only look's) listing of the queue; the function
   * it returns, if any, is called with that listing. The local view re-syncs from it (entries it does
   * not track yet, entries that left the store unseen). An observer only: a throw is caught and logged.
   */
  observeListing?: () => ((slots: readonly OutboxSlot[]) => void) | null
  /** Connectivity evidence for escalation; defaults to `navigator.onLine` (null on native). */
  isOnline?: () => boolean | null
  now?: () => number
  setTimer?: (fn: () => void, ms: number) => unknown
  clearTimer?: (handle: unknown) => void
  /** Web Locks; defaults to `navigator.locks` when present. `null` = none (native). */
  locks?: LockManagerLike | null
}

export interface OutboxDrainer {
  /** Run a pass (merged into the running one if any). Never rejects. */
  drain(trigger: DrainTrigger): Promise<void>
  status(): OutboxQueueStatus
  subscribe(listener: (status: OutboxQueueStatus) => void): () => void
  /** Lift a `master-changed` pause (P5, after the flag was re-read and this session keeps draining). */
  resumeAfterMasterCheck(): void
  /**
   * P9 — invariant 12's send (`exclusiveSend` below): one master write that is NOT queued, sent in this
   * drainer's single-flight slot, under its drain lock, and only behind an EMPTY queue. Never rejects.
   */
  sendExclusive(request: OutboxRequest, options: { kind: OutboxWriteKind; label: string }): Promise<ExclusiveSendResult>
  /**
   * Stop for good: no new pass, no timers. Settles once the pass in flight (if any) has ended — its
   * send returned and its outcome recorded. A later drainer for the organization waits for it.
   * That wait is bounded only by the transport: a send in flight ends at the replay timeout (the
   * api-client write timeout), so a caller such as sign-out must not block the UI on this promise
   * (P5: stop, then proceed; the next drainer for the org is the one that waits).
   */
  stop(): Promise<void>
}

/**
 * What `sendExclusive` did (P9). `not-sent` means nothing left the device:
 *  - `queue-held` (P9 review 1, S2-a): the queue holds an entry no drain will clear without a person — a
 *    HELD entry (a deterministic refusal, an operator-only absolute stock hold, a foreign-install hold not
 *    yet adopted), an absolute stock request that is not `live` (held on sight, `absolute-replay`), or an
 *    UNREADABLE record (`blocked`). The queue cannot empty, so waiting for it is pointless: the write is
 *    refused at once. `stoppedBy` names the first such record in queue order (what a person must resolve);
 *    `depth` is the queue's length. A held entry whose 30-minute retry happens to be due still counts: it
 *    was refused once, and a person must see that before a void goes behind it (one held entry stops all
 *    sync from the device — §9 risk 3, accepted).
 *  - `queue-not-empty`: the organization's queue holds writes made before this one that a drain can still
 *    deliver; sending now would overtake them (a void ahead of the sale it voids). `depth` is how many.
 *  - `other-tab`: another tab holds the drain lock (web); it may be delivering this queue right now.
 *  - `migrating`: a v1 → v2 migration of the organization is copying (P8): its copies go first.
 *  - `paused`: the drainer is paused on a write-master change (P5 re-reads the flag).
 *  - `stopped`: the drainer was stopped (sign-out, organization switch).
 *  - `storage`: the queue could not be read, so "empty" could not be established.
 * `sent` carries the classified outcome (`outboxClassify.ts`) and, when delivered, the response.
 */
export type ExclusiveSendResult =
  | { kind: 'sent'; outcome: DeliveryOutcome; response: unknown }
  | { kind: 'not-sent'; why: 'queue-held'; depth: number; stoppedBy: QueueHeldBy }
  | { kind: 'not-sent'; why: 'queue-not-empty' | 'other-tab' | 'migrating' | 'paused' | 'stopped' | 'storage'; depth: number | null }

/** What keeps a queue from ever emptying on its own (`queue-held`): the `stoppedBy` a pass would publish. */
export type QueueHeldBy = 'held' | 'blocked' | 'foreign-install'

/**
 * The first record of the queue (in queue order) that no drain clears without a person, or null when every
 * record can still be delivered by a drain (P9 review 1, S2-a). Mirrors what `pass` does with a head:
 *  - an unreadable record → `blocked` (only `removeCorrupt` clears it);
 *  - an entry another install queued → `foreign-install` (held until an operator adopts it);
 *  - a `held` entry → `held`, EXCEPT a foreign-install hold an operator has since adopted (no longer
 *    `foreignInstall`): `pass` sends that one at once (`adoptedForeignHold`);
 *  - an absolute stock request whose origin is not `live` → `held` (`pass` holds it on sight).
 */
export function queueHeldBy(slots: readonly OutboxSlot[]): QueueHeldBy | null {
  for (const slot of slots) {
    if (slot.status !== 'ok') return 'blocked'
    if (slot.foreignInstall) return 'foreign-install'
    const entry = slot.entry
    if (entry.state === 'held' && entry.lastError?.code !== FOREIGN_INSTALL_CODE) return 'held'
    if (entry.origin !== 'live' && isAbsoluteStockRequest(entry.request)) return 'held'
  }
  return null
}

/** A second live drainer for one organization in one JS context (review 1 S2). */
export class OutboxDrainerConflictError extends Error {
  constructor(public readonly orgFirestoreId: string) {
    super(`an outbox v2 drainer is already running for organization ${orgFirestoreId}; stop() it first`)
    this.name = 'OutboxDrainerConflictError'
  }
}

interface RegistryRecord {
  stopped: boolean
  /** Set by `stop()`: settles when this drainer (and every predecessor it waited for) has ended. */
  ended: Promise<void> | null
}

/** One per organization per JS context. Module state on purpose: that IS the JS context. */
const drainerRegistry = new Map<string, RegistryRecord>()

function sameError(a: OutboxLastError | null, b: OutboxLastError): boolean {
  return a !== null && a.status === b.status && a.code === b.code
}

export function createOutboxDrainer(options: OutboxDrainerOptions): OutboxDrainer {
  const org = options.orgFirestoreId
  const previous = drainerRegistry.get(org)
  if (previous && !previous.stopped) throw new OutboxDrainerConflictError(org)
  const predecessorEnded: Promise<void> = previous?.ended ?? Promise.resolve()
  const record: RegistryRecord = { stopped: false, ended: null }
  drainerRegistry.set(org, record)

  const now = options.now ?? Date.now
  const setTimer = options.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms))
  const clearTimer = options.clearTimer ?? ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>))
  const locks = options.locks === undefined ? defaultLocks() : options.locks
  const isOnline = options.isOnline ?? defaultIsOnline
  const sink = options.sink
  const ledger = options.alertLedger

  let status: OutboxQueueStatus = EMPTY_STATUS
  const listeners = new Set<(s: OutboxQueueStatus) => void>()
  let paused: 'master-changed' | null = null
  let stopped = false
  let running: Promise<void> | null = null
  let rerun: { backoff: boolean; held: boolean } | null = null
  /**
   * P8 review 3, SF-1: the release flags of every pass that stopped for the v1 → v2 migration whose
   * settlement is `settled`, merged. ONE resume per migration carries them (see `resumeAfterMigration`).
   */
  let migrationResume: { settled: Promise<void>; release: { backoff: boolean; held: boolean } } | null = null
  let timer: { handle: unknown; at: number } | null = null
  let lastStateReportAt: number | null = null
  /** Escalation clocks (see the module docblock); in memory on purpose. */
  let onlineSince: number | null = null
  let lastDeliveredAt: number | null = null
  /** The head entry the current `escalated` status refers to. */
  let escalatedEntryId: string | null = null
  let waitedForPredecessor = false

  /** Alert once per key, across restarts. A ledger that cannot answer never silences an alert. */
  async function once(key: string, alert: () => void): Promise<void> {
    let seen = false
    try {
      seen = await ledger.seen(key)
    } catch {
      seen = false
    }
    if (seen) return
    alert()
    try {
      await ledger.remember(key)
    } catch {
      // It may page again after a restart; that is the safe direction.
    }
  }

  function publish(next: OutboxQueueStatus): void {
    status = Object.freeze(next)
    sink.superProperties({
      outbox_v2_depth: next.depth,
      outbox_v2_oldest_age_ms: next.oldestEnqueuedAt === null ? null : Math.max(0, now() - next.oldestEnqueuedAt),
    })
    for (const listener of listeners) {
      try {
        listener(status)
      } catch {
        // A listener's failure never touches the queue.
      }
    }
  }

  /**
   * Feed the connectivity evidence of one observation: `answered` is `true` when a server answered a
   * send (any status), `false` when a send got no answer, `null` when nothing was sent.
   */
  function observeConnectivity(answered: boolean | null, t: number): void {
    const nav = isOnline()
    if (answered === true || nav === true) {
      if (onlineSince === null) onlineSince = t
      return
    }
    if (answered === false || nav === false) onlineSince = null
  }

  /** Did a server answer this send? (A delivery, or any outcome that carries an HTTP status.) */
  function answeredBy(outcome: DeliveryOutcome): boolean {
    return outcome.kind === 'delivered' || outcome.status !== null
  }

  /** Escalation, evaluated only at a STOP point whose head is merely retrying. */
  function escalationAt(oldest: number | null, stoppedBy: OutboxQueueStatus['stoppedBy'], t: number): boolean {
    if (stoppedBy !== null || oldest === null || onlineSince === null) return false
    const since = Math.max(oldest, onlineSince, lastDeliveredAt ?? 0)
    return t - since >= ESCALATE_AFTER_MS
  }

  /**
   * `atStop`: this is where the pass stops (or a read-only look), so escalation is evaluated. Before a
   * send (`atStop` false) the flag is kept only for the very head entry that escalated.
   */
  function statusOf(
    slots: OutboxSlot[],
    stoppedBy: OutboxQueueStatus['stoppedBy'],
    atStop: boolean,
    readOnly = false,
  ): OutboxQueueStatus {
    let held = 0
    let operatorHeld = 0
    let blocked = 0
    let foreignInstall = 0
    let oldest: number | null = null
    for (const slot of slots) {
      if (slot.status !== 'ok') {
        blocked += 1
        continue
      }
      if (slot.entry.state === 'held') {
        held += 1
        if (isOperatorOnlyHold(slot.entry)) operatorHeld += 1
      }
      if (slot.foreignInstall) foreignInstall += 1
      if (oldest === null || slot.entry.enqueuedAt < oldest) oldest = slot.entry.enqueuedAt
    }
    const head = slots[0]
    const headId = head && head.status === 'ok' ? head.entry.entryId : null
    let escalated: boolean
    if (atStop) {
      escalated = escalationAt(oldest, stoppedBy, now())
      escalatedEntryId = escalated ? headId : null
    } else {
      escalated = status.escalated && headId !== null && headId === escalatedEntryId
    }
    return {
      depth: slots.length,
      held,
      operatorHeld,
      blocked,
      foreignInstall,
      oldestEnqueuedAt: oldest,
      stoppedBy,
      paused,
      escalated,
      known: true,
      readOnly,
    }
  }

  /** The drain lock holder publishes and pages; a read-only tab only publishes. */
  async function publishAndEscalate(slots: OutboxSlot[], stoppedBy: OutboxQueueStatus['stoppedBy']): Promise<void> {
    const next = statusOf(slots, stoppedBy, true)
    publish(next)
    const head = slots[0]
    if (!next.escalated || !head) return
    const key = head.status === 'ok' ? `escalated:${org}:${head.entry.entryId}` : `escalated:${org}:${head.ref}`
    await once(key, () =>
      sink.escalated({
        label: head.status === 'ok' ? head.entry.label : `unreadable-${head.status}`,
        ageMs: next.oldestEnqueuedAt === null ? 0 : Math.max(0, now() - next.oldestEnqueuedAt),
        depth: next.depth,
        state: head.status === 'ok' ? head.entry.state : head.status,
        status: head.status === 'ok' ? head.entry.lastError?.status ?? null : null,
        code: head.status === 'ok' ? head.entry.lastError?.code ?? null : null,
        attempts: head.status === 'ok' ? head.entry.attempts : 0,
      }),
    )
  }

  /** What stops the head, as a read-only observer sees it (no state is changed). */
  function observedStop(slots: OutboxSlot[]): OutboxQueueStatus['stoppedBy'] {
    const head = slots[0]
    if (!head) return null
    if (paused) return 'master-changed'
    if (head.status !== 'ok') return 'blocked'
    if (head.foreignInstall) return 'foreign-install'
    return head.entry.state === 'held' ? 'held' : null
  }

  function schedule(at: number): void {
    if (stopped) return
    if (timer && timer.at <= at) return
    if (timer) clearTimer(timer.handle)
    const delay = Math.max(0, at - now())
    const handle = setTimer(() => {
      timer = null
      void drain('timer')
    }, delay)
    timer = { handle, at }
  }

  async function reportStateIfDue(queueStore: OutboxStore): Promise<void> {
    const t = now()
    if (lastStateReportAt !== null && t - lastStateReportAt < STATE_REPORT_INTERVAL_MS) return
    lastStateReportAt = t
    let otherOrganizations = 0
    let unattributed = 0
    try {
      const census = await queueStore.census()
      otherOrganizations = census.organizations.filter((o) => o !== org).length
      unattributed = census.unattributed.length
    } catch {
      // The state report is best-effort; the queue's own status was already published.
    }
    const report: OutboxStateReport = {
      depth: status.depth,
      held: status.held,
      blocked: status.blocked,
      foreignInstall: status.foreignInstall,
      oldestAgeMs: status.oldestEnqueuedAt === null ? null : Math.max(0, t - status.oldestEnqueuedAt),
      paused,
      escalated: status.escalated,
      otherOrganizations,
      unattributed,
    }
    sink.state(report)
  }

  /** P9: the response travels with the outcome, for the seam's `fromAck` (`onDelivered`). */
  async function send(entry: OutboxEntryV2): Promise<{ outcome: DeliveryOutcome; response: unknown }> {
    try {
      const response = await options.send(entry.request, { writeMode: writeModeFor(entry.origin) })
      return { outcome: classifySuccess(entry, response), response }
    } catch (error) {
      return { outcome: classifyFailure(entry, error), response: undefined }
    }
  }

  function heldAlert(entry: OutboxEntryV2, reason: HeldReason, lastError: OutboxLastError, at: number) {
    return {
      label: entry.label,
      kind: entry.kind,
      origin: entry.origin,
      reason,
      status: lastError.status,
      code: lastError.code,
      attempts: entry.attempts,
      ageMs: Math.max(0, at - entry.enqueuedAt),
      operatorOnly: isOperatorOnlyHold(entry),
      entryId: entry.entryId,
      documentId: entry.documentId,
    }
  }

  /** List the queue, telling `observeListing` (P6) — whose failures never reach the drain. */
  async function listObserved(queueStore: OutboxStore): Promise<OutboxSlot[]> {
    let observer: ((slots: readonly OutboxSlot[]) => void) | null = null
    try {
      observer = options.observeListing ? options.observeListing() : null
    } catch (error) {
      console.error('[outboxV2] the listing observer threw; the drain is unaffected', { org, error })
    }
    const slots = await queueStore.list(org)
    if (observer) {
      try {
        observer(slots)
      } catch (error) {
        console.error('[outboxV2] the listing observer threw; the drain is unaffected', { org, error })
      }
    }
    return slots
  }

  /** One pass over the head of the queue until it stops. */
  async function pass(release: { backoff: boolean; held: boolean }): Promise<void> {
    const store = options.openStore()
    let releaseBackoff = release.backoff
    let releaseHeld = release.held
    observeConnectivity(null, now())
    try {
      for (;;) {
        if (stopped) return
        if (paused) {
          publish({ ...status, stoppedBy: 'master-changed', paused })
          return
        }
        // P8 (reviews 1–2, S-1 / SF-1): never deliver while a v1 → v2 migration of this organization
        // is copying — its copies leave v1 only once it ends, so a delivery now could be replayed by v1
        // after a crash (a delta applied twice). Checked on EVERY iteration, before the next entry is
        // marked in flight, because a migration can start mid-pass (a re-pin on a `[db, user]` change).
        // The pass ENDS rather than waiting, so it releases the drain lock the migration takes on web
        // (no tab drains this organization meanwhile), and resumes once the migration has settled —
        // with the release flags this pass still held (review 3, SF-1: an `app-start` that meets the
        // migration still retries a held entry at once afterwards; a held retry already spent in this
        // pass is not granted twice). Synchronous `null` when none runs (every session today): no
        // extra microtask.
        const migrating = v1MigrationInProgress(org)
        if (migrating) {
          await stopForMigration(store, migrating, { backoff: releaseBackoff, held: releaseHeld })
          return
        }
        const slots = await listObserved(store)
        if (slots.length === 0) {
          publish(statusOf(slots, null, true))
          return
        }
        const head = slots[0]
        if (head.status !== 'ok') {
          await publishAndEscalate(slots, 'blocked')
          await once(`blocked:${org}:${head.status}:${head.ref}:${head.reason}`, () =>
            sink.blocked({ kind: head.status, reason: head.reason, ref: head.ref }),
          )
          return
        }
        const entry = head.entry
        const t = now()

        if (head.foreignInstall) {
          const hold: OutboxLastError = { status: null, code: FOREIGN_INSTALL_CODE }
          if (entry.state !== 'held' || !sameError(entry.lastError, hold)) {
            await store.update(org, entry.entryId, {
              state: 'held',
              heldSince: entry.heldSince ?? t,
              lastError: hold,
              nextAttemptAt: t + HELD_RETRY_MS,
            })
            sink.held(heldAlert(entry, 'foreign-install', hold, t))
          }
          await publishAndEscalate(await store.list(org), 'foreign-install')
          return
        }

        // An absolute stock level this session's live writer did not produce is never replayed.
        if (entry.origin !== 'live' && isAbsoluteStockRequest(entry.request) && entry.state !== 'held') {
          const hold: OutboxLastError = { status: null, code: ABSOLUTE_REPLAY_CODE }
          await store.update(org, entry.entryId, { state: 'held', heldSince: entry.heldSince ?? t, lastError: hold })
          sink.held(heldAlert(entry, 'absolute-replay', hold, t))
          await publishAndEscalate(await store.list(org), 'held')
          return
        }

        // Adopted by an operator since it was held for being foreign: nothing left to wait for.
        const adoptedForeignHold = entry.state === 'held' && entry.lastError?.code === FOREIGN_INSTALL_CODE
        if (entry.state === 'held' && !adoptedForeignHold && isOperatorOnlyHold(entry)) {
          // Only `outboxOperator.resolveOperatorHold` moves it; no timer, no trigger.
          await publishAndEscalate(slots, 'held')
          return
        }
        if (entry.state === 'held' && !adoptedForeignHold && !releaseHeld && t < entry.nextAttemptAt) {
          await publishAndEscalate(slots, 'held')
          schedule(entry.nextAttemptAt)
          return
        }
        if (entry.state === 'pending' && !releaseBackoff && t < entry.nextAttemptAt) {
          await publishAndEscalate(slots, null)
          schedule(entry.nextAttemptAt)
          return
        }
        publish(statusOf(slots, null, false))

        // Held for a REFUSAL (a person must see it) — as opposed to a foreign-install hold an operator
        // has since lifted by adopting the entry. An in-flight entry with `heldSince` was a held retry
        // the process did not see finish.
        const wasHeld =
          (entry.state === 'held' || entry.state === 'in-flight') &&
          entry.heldSince !== null &&
          entry.lastError?.code !== FOREIGN_INSTALL_CODE
        const marked = await store.update(org, entry.entryId, { state: 'in-flight', attempts: entry.attempts + 1 })
        if (!marked) continue // purged or removed meanwhile; never resurrected
        releaseHeld = false

        const { outcome, response } = await send(marked)
        const after = now()
        observeConnectivity(answeredBy(outcome), after)
        switch (outcome.kind) {
          case 'delivered': {
            if (options.onDelivered) {
              try {
                options.onDelivered(marked, after, response)
              } catch (error) {
                console.error('[outboxV2] the delivery observer threw; delivery is unaffected', { org, entryId: entry.entryId, error })
              }
            }
            await store.remove(org, entry.entryId)
            lastDeliveredAt = after
            escalatedEntryId = null
            releaseBackoff = true
            continue
          }
          case 'retry': {
            const lastError: OutboxLastError = { status: outcome.status, code: outcome.code }
            if (wasHeld) {
              // Still held: the refusal is what a person must see, not this transport failure.
              await store.update(org, entry.entryId, { state: 'held', nextAttemptAt: after + HELD_RETRY_MS })
              await publishAndEscalate(await store.list(org), 'held')
              if (!isOperatorOnlyHold(entry)) schedule(after + HELD_RETRY_MS)
            } else {
              const nextAttemptAt = after + backoffMs(marked.attempts)
              await store.update(org, entry.entryId, { state: 'pending', lastError, heldSince: null, nextAttemptAt })
              await publishAndEscalate(await store.list(org), null)
              schedule(nextAttemptAt)
            }
            return
          }
          case 'held': {
            const lastError: OutboxLastError = { status: outcome.status, code: outcome.code }
            const alreadyAlerted = wasHeld && sameError(entry.lastError, lastError)
            await store.update(org, entry.entryId, {
              state: 'held',
              heldSince: wasHeld && entry.heldSince !== null ? entry.heldSince : after,
              lastError,
              nextAttemptAt: after + HELD_RETRY_MS,
            })
            if (!alreadyAlerted) sink.held(heldAlert(marked, outcome.reason, lastError, after))
            if (options.onHeld) {
              try {
                options.onHeld(marked, lastError, after)
              } catch (error) {
                console.error('[outboxV2] the hold observer threw; the hold is unaffected', { org, entryId: entry.entryId, error })
              }
            }
            await publishAndEscalate(await store.list(org), 'held')
            if (!isOperatorOnlyHold(entry)) schedule(after + HELD_RETRY_MS)
            return
          }
          case 'master-changed': {
            // A held entry keeps its refusal as `lastError`: that is what a person must see.
            await store.update(
              org,
              entry.entryId,
              wasHeld
                ? { state: 'held' }
                : { state: 'pending', heldSince: null, lastError: { status: outcome.status, code: outcome.code } },
            )
            paused = 'master-changed'
            publish(statusOf(await store.list(org), 'master-changed', true))
            sink.masterChanged({ label: entry.label, status: outcome.status, code: outcome.code })
            try {
              options.onMasterChanged?.({ code: outcome.code, status: outcome.status, label: entry.label })
            } catch {
              // The pause stands whatever the callback does.
            }
            return
          }
        }
      }
    } finally {
      try {
        await reportStateIfDue(store)
      } catch {
        // best-effort
      }
    }
  }

  /**
   * P8 review 3, SF-1: run ONE more pass once the migration behind `settled` has settled, carrying
   * `release` (merged with every other pass that stopped for the same migration). One resume per
   * migration: a later stop for the same settlement only merges its flags. If a run is going when it
   * fires, it is merged into that run's `rerun` like any trigger — so at most one extra pass.
   * Registered BEFORE anything is awaited, so a migration that settles meanwhile is never missed.
   */
  function resumeAfterMigration(settled: Promise<void>, release: { backoff: boolean; held: boolean }): void {
    if (migrationResume && migrationResume.settled === settled) {
      migrationResume.release = {
        backoff: migrationResume.release.backoff || release.backoff,
        held: migrationResume.release.held || release.held,
      }
      return
    }
    const pending = { settled, release }
    migrationResume = pending
    void settled.then(() => {
      if (migrationResume === pending) migrationResume = null
      void drainWith(pending.release)
    })
  }

  /**
   * A pass (or the lock request before it) met a running migration: register the resume, then publish
   * the queue as `v1-migration` (review 3, nit 1) — read-only, best-effort, never `readOnly: true`: this
   * tab is not refused by another tab, it waits for its own migration. `passStore` is the stopped pass's
   * store, or null to open one (not named `store`: the W-writer census exempts `store.update` here only
   * because every `store` in this function is `options.openStore()`, scripts/wWriterCensus.ts).
   */
  async function stopForMigration(
    passStore: OutboxStore | null,
    settled: Promise<void>,
    release: { backoff: boolean; held: boolean },
  ): Promise<void> {
    resumeAfterMigration(settled, release)
    try {
      const slots = await listObserved(passStore ?? options.openStore())
      // `atStop: false`: a migration is not a stop that escalates, and an escalated head keeps its flag.
      publish(statusOf(slots, 'v1-migration', false))
    } catch {
      // The status is best-effort; the resume is already registered.
    }
  }

  /** Another tab drains: read the queue and publish, change nothing (review 1 S7). */
  async function publishReadOnly(): Promise<void> {
    try {
      const store = options.openStore()
      const slots = await listObserved(store)
      observeConnectivity(null, now())
      publish(statusOf(slots, observedStop(slots), true, true))
    } catch {
      // Nothing to show is better than a wrong status; the next look retries.
    }
  }

  async function passUnderLock(release: { backoff: boolean; held: boolean }): Promise<void> {
    try {
      // P8 review 3, SF-1: a migration of this organization in THIS context — on web it holds (or
      // waits for) the very lock requested below, so asking would be refused and the flags lost to a
      // read-only look (`readOnly: true` in the migrating tab itself, for up to `RECHECK_MS`). Wait for
      // it instead, without asking. Synchronous `null` when none runs (every session today).
      const migrating = v1MigrationInProgress(org)
      if (migrating) {
        await stopForMigration(null, migrating, release)
        return
      }
      if (!locks) {
        await pass(release)
        return
      }
      let granted = false
      await locks.request(outboxV2DrainLockName(org), { ifAvailable: true }, async (lock) => {
        if (!lock) return
        granted = true
        await pass(release)
      })
      if (!granted) {
        // Never this context's own migration: it registers its barrier synchronously BEFORE it asks for
        // the lock, and the check above runs in the same synchronous step as this request — so a
        // refusal here is another tab's drainer or another tab's migration.
        await publishReadOnly()
        schedule(now() + RECHECK_MS)
      }
    } catch (error) {
      // Storage refused (closed store, quota, IndexedDB unavailable). Nothing was dropped: the
      // entries are where they were. Look again later.
      // eslint-disable-next-line no-console
      console.warn('[outboxV2] drain pass failed; will retry', error)
      schedule(now() + RECHECK_MS)
    }
  }

  /** Read and clear the merged triggers (a function, so the read is not narrowed away by TypeScript). */
  function takeRerun(): { backoff: boolean; held: boolean } | null {
    const taken = rerun
    rerun = null
    return taken
  }

  function drain(trigger: DrainTrigger): Promise<void> {
    return drainWith({ backoff: RELEASES_BACKOFF.has(trigger), held: RELEASES_HELD.has(trigger) })
  }

  /** `drain` by release flags rather than by trigger (the migration resume carries a stopped pass's). */
  function drainWith(release: { backoff: boolean; held: boolean }): Promise<void> {
    if (stopped) return Promise.resolve()
    if (running) {
      rerun = rerun ? { backoff: rerun.backoff || release.backoff, held: rerun.held || release.held } : release
      return running
    }
    rerun = null
    running = (async () => {
      let next: { backoff: boolean; held: boolean } | null = release
      if (!waitedForPredecessor) {
        // Never overlap the drainer this one replaces (review 1 S2). Triggers that arrive while
        // waiting are merged into the first pass, not discarded.
        await predecessorEnded
        waitedForPredecessor = true
        const early = takeRerun()
        if (early) next = { backoff: next.backoff || early.backoff, held: next.held || early.held }
      }
      while (next && !stopped) {
        await passUnderLock(next)
        next = takeRerun()
      }
      // In the same tick as the loop's exit decision: a trigger after this line starts a new run,
      // one before it was merged into `rerun` and ran above (review 1 S3).
      running = null
    })()
    return running
  }

  /**
   * P9 — invariant 12 (stage-0 §4.2): a money-moving delete fails visibly offline and applies nothing.
   * Such a write cannot be QUEUED (a queued reversal would apply later, after the cashier was told it
   * failed — stage-0 defects 8–9), so it is sent once, now. It must still keep the queue's order: sent
   * while earlier writes of this device wait in the queue, a void would reach the backend AHEAD of the
   * sale it voids. So it is sent only when this organization's queue is EMPTY, from this drainer's
   * single-flight slot (no queued entry is in flight beside it, and triggers that arrive meanwhile run
   * as a pass afterwards), under the drain Web Lock on web (no other tab's drainer sends beside it), and
   * never while a v1 → v2 migration copies or a write-master change pauses the drain.
   *
   * The outcome is classified exactly as a queued entry's would be. `master-changed` pauses the drainer
   * and asks for the flag re-read, as a pass does. Nothing is stored: the write was never queued.
   */
  async function exclusiveOnce(request: OutboxRequest, kind: OutboxWriteKind, label: string): Promise<ExclusiveSendResult> {
    if (paused) return { kind: 'not-sent', why: 'paused', depth: null }
    if (v1MigrationInProgress(org)) return { kind: 'not-sent', why: 'migrating', depth: null }
    const attempt = async (): Promise<ExclusiveSendResult> => {
      let slots: OutboxSlot[]
      try {
        slots = await listObserved(options.openStore())
      } catch {
        return { kind: 'not-sent', why: 'storage', depth: null }
      }
      if (slots.length > 0) {
        publish(statusOf(slots, observedStop(slots), true))
        // P9 review 1, S2-a: a queue that no drain can empty refuses at once, with what stops it.
        const heldBy = queueHeldBy(slots)
        if (heldBy) return { kind: 'not-sent', why: 'queue-held', depth: slots.length, stoppedBy: heldBy }
        return { kind: 'not-sent', why: 'queue-not-empty', depth: slots.length }
      }
      let response: unknown
      let outcome: DeliveryOutcome
      try {
        response = await options.send(request, { writeMode: 'master' })
        outcome = classifySuccess({ request }, response)
      } catch (error) {
        response = undefined
        outcome = classifyFailure({ kind }, error)
      }
      const after = now()
      observeConnectivity(answeredBy(outcome), after)
      if (outcome.kind === 'delivered') lastDeliveredAt = after
      if (outcome.kind === 'master-changed') {
        paused = 'master-changed'
        publish({ ...status, stoppedBy: 'master-changed', paused })
        sink.masterChanged({ label, status: outcome.status, code: outcome.code })
        try {
          options.onMasterChanged?.({ code: outcome.code, status: outcome.status, label })
        } catch {
          // The pause stands whatever the callback does.
        }
      }
      return { kind: 'sent', outcome, response }
    }
    if (!locks) return attempt()
    let granted = false
    let result: ExclusiveSendResult = { kind: 'not-sent', why: 'other-tab', depth: null }
    await locks.request(outboxV2DrainLockName(org), { ifAvailable: true }, async (lock) => {
      if (!lock) return
      granted = true
      result = await attempt()
    })
    return granted ? result : { kind: 'not-sent', why: 'other-tab', depth: null }
  }

  async function sendExclusive(request: OutboxRequest, sendOptions: { kind: OutboxWriteKind; label: string }): Promise<ExclusiveSendResult> {
    // The single-flight slot: wait for the run in progress (a pass, or another exclusive send).
    for (;;) {
      if (stopped) return { kind: 'not-sent', why: 'stopped', depth: null }
      const current = running
      if (!current) break
      await current
    }
    let result: ExclusiveSendResult = { kind: 'not-sent', why: 'stopped', depth: null }
    rerun = null
    running = (async () => {
      if (!waitedForPredecessor) {
        await predecessorEnded
        waitedForPredecessor = true
      }
      try {
        result = await exclusiveOnce(request, sendOptions.kind, sendOptions.label)
      } catch (error) {
        // `exclusiveOnce` reports every failure as a result; a throw here is a lock-manager failure.
        console.warn('[outboxV2] the exclusive send could not run', { org, error })
        result = { kind: 'not-sent', why: 'storage', depth: null }
      }
      // Triggers that arrived while the slot was held are a pass now, exactly as `drainWith` merges them.
      let next = takeRerun()
      while (next && !stopped) {
        await passUnderLock(next)
        next = takeRerun()
      }
      running = null
    })()
    await running
    return result
  }

  return {
    sendExclusive,
    drain,
    status: () => status,
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    resumeAfterMasterCheck() {
      if (paused === null) return
      paused = null
      void drain('manual')
    },
    stop() {
      if (record.ended) return record.ended
      stopped = true
      record.stopped = true
      if (timer) clearTimer(timer.handle)
      timer = null
      listeners.clear()
      const inFlight = running ?? Promise.resolve()
      record.ended = Promise.all([inFlight, predecessorEnded]).then(() => {
        if (drainerRegistry.get(org) === record) drainerRegistry.delete(org)
      })
      return record.ended
    },
  }
}

/**
 * Diagnostics: the organizations with a registered drainer, and whether each is stopping. A stopped
 * drainer leaves the registry once its `stop()` has settled (P5 can log this on sign-out).
 */
export function outboxDrainerRegistrySnapshot(): Array<{ orgFirestoreId: string; stopping: boolean }> {
  return [...drainerRegistry.entries()].map(([orgFirestoreId, r]) => ({ orgFirestoreId, stopping: r.stopped }))
}

/** Test isolation only: forget every registered drainer (does not stop them). */
export function resetOutboxDrainerRegistryForTests(): void {
  drainerRegistry.clear()
}
